import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FileUp, Loader2, Save, IndianRupee, ChevronRight } from 'lucide-react';

import {
    fetchUploadDocsMoids,
    fetchUploadLimitGrid,
    fetchMoidAmount,
    submitPosUploadAmountLimit,
    clearCurrentAmount,
    clearPosUploadSaveResult,
    selectMoidList,
    selectUploadGridList,
    selectCurrentAmount,
    selectPosUploadSaveResult,
    selectMoidsLoading,
    selectGridLoading,
    selectAmountLoading,
    selectSaveLoading,
    selectGridError,
    selectSaveError,
} from '../../slices/accountsSlice/posUploadDocsLimitConfigSlice';
import { showToast } from '../../utilities/toastUtilities';

const currencyFmt = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
});

const PosUploadDocsLimitConfig = () => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userDetails?.userName || 'system';

    const moidList  = useSelector(selectMoidList);
    const gridList  = useSelector(selectUploadGridList);
    const currentAmount = useSelector(selectCurrentAmount);
    const saveResult    = useSelector(selectPosUploadSaveResult);

    const moidsLoading  = useSelector(selectMoidsLoading);
    const gridLoading   = useSelector(selectGridLoading);
    const gridError     = useSelector(selectGridError);
    const amountLoading = useSelector(selectAmountLoading);
    const saveLoading   = useSelector(selectSaveLoading);
    const saveError     = useSelector(selectSaveError);

    const [moid, setMoid] = useState('');
    const [amount, setAmount] = useState('');

    useEffect(() => {
        dispatch(fetchUploadDocsMoids());
        dispatch(fetchUploadLimitGrid());
    }, [dispatch]);

    useEffect(() => {
        setAmount(currentAmount ?? '');
    }, [currentAmount]);

    useEffect(() => {
        if (saveResult) {
            showToast('success', 'PO upload document amount limit saved successfully.');
            dispatch(clearPosUploadSaveResult());
            dispatch(fetchUploadLimitGrid());
        }
        if (saveError) {
            showToast('error', typeof saveError === 'string' ? saveError : 'Failed to save the amount limit.');
            dispatch(clearPosUploadSaveResult());
        }
    }, [saveResult, saveError, dispatch]);

    const selectedMoidText = useMemo(
        () => moidList.find(m => m.Moidvalue === moid)?.MoidText || '',
        [moidList, moid]
    );

    const handleMoidChange = (e) => {
        const value = e.target.value;
        setMoid(value);
        dispatch(clearCurrentAmount());
        setAmount('');
        if (value) {
            dispatch(fetchMoidAmount(value));
        }
    };

    const handleSubmit = () => {
        if (!moid) {
            showToast('error', 'Please select a Master Operation.');
            return;
        }
        const newVal = parseFloat(amount);
        if (amount === '' || isNaN(newVal) || newVal < 0) {
            showToast('error', 'Please enter a valid amount.');
            return;
        }

        dispatch(submitPosUploadAmountLimit({
            code:      moid,
            value:     String(newVal),
            Createdby: currentUser,
        }));
    };

    const canSubmit = moid && amount !== '' && !isNaN(parseFloat(amount));

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* Page Header */}
            <div className="max-w-5xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-6 text-white">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20 flex-shrink-0">
                            <FileUp className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">
                                Configuration
                            </span>
                            <h1 className="text-xl md:text-2xl font-black tracking-tight mt-1">PO's Upload Document Amount Limit Configuration</h1>
                            <p className="text-indigo-200 text-sm mt-0.5">
                                Set, per approval operation, the PO value above which a supporting document must be uploaded.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto space-y-4">

                {/* Selection form */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* MOID */}
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                Master Operation
                            </label>
                            <select
                                value={moid}
                                onChange={handleMoidChange}
                                disabled={moidsLoading}
                                className="w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60"
                            >
                                <option value="">-Please Select-</option>
                                {moidList.map((m) => (
                                    <option key={m.Moidvalue} value={m.Moidvalue}>{m.MoidText}</option>
                                ))}
                            </select>
                        </div>

                        {/* Amount */}
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                Upload Document Amount Limit
                            </label>
                            <div className="relative">
                                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
                                    disabled={!moid || amountLoading}
                                    placeholder="0.00"
                                    className="w-full pl-8 pr-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60"
                                />
                            </div>
                            {amountLoading && (
                                <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1">
                                    <Loader2 className="h-3 w-3 animate-spin" /> Loading current amount…
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 mt-4">
                        <button
                            onClick={handleSubmit}
                            disabled={!canSubmit || saveLoading}
                            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {saveLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                            Submit
                        </button>
                    </div>
                </div>

                {/* Grid of configured limits */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                        <ChevronRight className="h-4 w-4 text-indigo-500" />
                        <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200">Configured Upload Limits</h2>
                    </div>

                    {gridLoading ? (
                        <div className="py-10 flex flex-col items-center justify-center gap-2">
                            <Loader2 className="h-5 w-5 text-indigo-500 animate-spin" />
                            <span className="text-xs text-gray-400">Loading configured limits…</span>
                        </div>
                    ) : gridError ? (
                        <div className="py-8 text-center text-sm text-rose-500">{gridError}</div>
                    ) : gridList.length === 0 ? (
                        <div className="py-10 text-center text-gray-400 dark:text-gray-500 text-sm">
                            No upload limits configured yet.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">MOID</th>
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Master Operation</th>
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">Upload Limit</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                    {gridList.map((row, idx) => (
                                        <tr key={`${row.code}-${idx}`} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                            <td className="px-4 py-3 font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap">{row.code}</td>
                                            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{row.name}</td>
                                            <td className="px-4 py-3 text-right font-semibold text-indigo-700 dark:text-indigo-400 whitespace-nowrap">
                                                {row.value != null ? currencyFmt.format(row.value) : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PosUploadDocsLimitConfig;
